// ─── Cuenta corriente del empleado — imputación open-item (partida abierta) ──────────────────
//
// Fuente ÚNICA de verdad del saldo de sueldo por empleado/mes. La consumen todas las vistas
// (FlujoCajaV2 "Movimientos Futuros" y el módulo Empleados) para que Caja y Empleados no puedan
// divergir (regla 10.1).
//
// Reemplaza el atajo booleano `mesConSueldoPagado` (commit e377dfba): éste marcaba el mes como
// saldado si existía ≥1 pago tipo='sueldo' del período SIN mirar el monto, así que un pago parcial
// ocultaba la fila entera (la regresión que motivó esta util). Acá NO hay atajo "mes saldado": la
// fila se emite/oculta según `saldo > tolerancia`, derivado de la aritmética del FIFO.
//
// Modelo (política contable confirmada por Ignacio, 2026-08-07 — "anclar al recibo"):
//   • Devengado por mes:
//       – Mes CERRADO (recibo emitido = tiene un pago tipo='sueldo' registrado en el período):
//         el devengado es el LÍQUIDO efectivamente transferido para ese mes = Σ(sueldo+adelanto)
//         del período. Es el recibo registrado, NO el recálculo del motor. Anclar al recibo evita
//         que una diferencia de recálculo (motor con parámetros/insumos de hoy vs. el recibo
//         original) reabra un "saldo fantasma" en un mes ya liquidado. `pagos_empleados` guarda el
//         LÍQUIDO transferido, no el devengado (regla 10.62), y no hay tabla de recibos: por eso el
//         recibo se reconstruye desde Σ(sueldo+adelanto) del período.
//       – Mes EN CURSO / FUTURO (sin recibo emitido): la estimación del motor
//         (`calcularLiquidacion<País>`), que el llamador computa y pasa como `devengadoMotor`.
//   • Bolsa = Σ de TODOS los pagos que componen líquido (tipo='sueldo' y tipo='adelanto' cuentan
//     igual), SIN filtrar por período. `transporte` es asignación aparte (reembolso), no compone
//     líquido; `comision`/`bono`/`finiquito` tampoco netean acá (idéntico a `sumarPagadoLiquido`).
//   • Imputación FIFO, del mes más antiguo al más nuevo: por cada mes
//         aplicado = min(bolsa, devengado);  saldo = devengado − aplicado;  bolsa −= aplicado.
//     El excedente tras el último mes es prepago (no genera fila; no rompe).
//
// Invariante: Σ saldos = Σ devengado − Σ aplicado  (aplicado total = bolsa − prepago).
//
// Excepción "neteo por motor directo" (Fase B scopeada, ROADMAP P3.17): para los empleados de la
// allowlist `EMPLEADOS_NETEO_MOTOR_DIRECTO` el ancla al recibo se APAGA — el devengado de cada mes es
// el del motor aun en meses cerrados. Sirve para que un pago parcial etiquetado `sueldo` no oculte el
// residual real (Ignacio julio 2026 = $647.968). Es TRANSITORIO: se retira cuando se reconcilien las
// deudas de datos de Franco (P3.15) y Nicolás (P3.16) y se pueda hacer la Fase B global.
//
// Forma lista para Perú: el llamador solo cambia cómo calcula `devengadoMotor` (motor del país);
// la imputación es idéntica.

import { EMPLEADOS_NETEO_MOTOR_DIRECTO } from "../constants/previsional";

// Tipos de pago que COMPONEN líquido (netean contra el devengado). Espejo de `sumarPagadoLiquido`
// en pagosEmpleados.js — mantener sincronizados (regla 10.1). `transporte` queda afuera a propósito
// (asignación de movilización, se paga aparte del sueldo).
export const TIPOS_COMPONEN_LIQUIDO = ["sueldo", "adelanto"];

// Tolerancia de redondeo del saldo, definida UNA vez y central. Un saldo por debajo de este umbral
// es ruido de redondeo del recálculo (AFP/salud/impuesto redondean al peso) o una miga del FIFO, no
// una deuda real: no se emite fila. ~$1.000 (moneda local; CLP/PEN son monedas sin decimales de uso
// corriente, así que el umbral es holgado frente al ruido de centenas del motor).
export const TOLERANCIA_SALDO_LIQUIDO = 1000;

const componeLiquido = (p) => TIPOS_COMPONEN_LIQUIDO.includes(p.tipo);

/**
 * Netea la cuenta corriente de UN empleado por imputación open-item (ver cabecera del archivo).
 *
 * @param {Object} params
 * @param {Array<{año:number, mes:number, devengadoMotor:number}>} params.devengados
 *        Meses a devengar (uno por período con remuneración vigente). `devengadoMotor` es el líquido
 *        estimado por el motor del país para ese mes; se usa tal cual en meses SIN recibo, y se
 *        ignora (se ancla al recibo) en meses CON recibo. Cualquier orden — la función ordena
 *        viejo→nuevo antes del FIFO.
 * @param {Array<{tipo:string, monto:number|string, periodo_año:number, periodo_mes:number}>} params.pagos
 *        Pagos del empleado (filas de `pagos_empleados`). La bolsa toma los que componen líquido sin
 *        filtrar por período; el recibo por mes sale de los que matchean el período.
 * @param {number} [params.tolerancia=TOLERANCIA_SALDO_LIQUIDO] Umbral bajo el cual `emitir=false`.
 * @param {string|number|null} [params.empleadoId=null] ID del empleado. Si está en la allowlist
 *        `EMPLEADOS_NETEO_MOTOR_DIRECTO` (Fase B scopeada, P3.17), el devengado de CADA mes es el
 *        del motor (`devengadoMotor`) aunque el mes esté "cerrado" — se desactiva el ancla al recibo
 *        para que un pago parcial no oculte el residual real. Resto de empleados: ancla intacta.
 * @returns {{subledger: Array<Object>, bolsa:number, aplicadoTotal:number, prepago:number, tolerancia:number}}
 *          Cada fila del subledger: {año, mes, devengado, aplicado, saldo, tieneRecibo, emitir}.
 */
export const netearEmpleado = ({
  devengados = [],
  pagos = [],
  tolerancia = TOLERANCIA_SALDO_LIQUIDO,
  empleadoId = null,
} = {}) => {
  // Fase B scopeada (P3.17): para los empleados de la allowlist el devengado sale SIEMPRE del motor
  // (no del recibo), aunque el mes esté cerrado. Comparación por ID (regla 10.7).
  const neteoMotorDirecto = empleadoId != null && EMPLEADOS_NETEO_MOTOR_DIRECTO.includes(empleadoId);
  // Bolsa = Σ de todos los pagos que componen líquido, SIN filtrar por período.
  const bolsa = (pagos || []).filter(componeLiquido).reduce((s, p) => s + (Number(p.monto) || 0), 0);

  // Recibo (Σ líquido transferido) e indicador de recibo emitido (∃ sueldo), por período.
  const liquidoDelPeriodo = new Map(); // "año-mes" -> Σ(sueldo+adelanto) del período
  const reciboEmitido = new Set(); // "año-mes" con ≥1 pago tipo='sueldo'
  for (const p of pagos || []) {
    if (!componeLiquido(p)) continue;
    const k = `${p.periodo_año}-${p.periodo_mes}`;
    liquidoDelPeriodo.set(k, (liquidoDelPeriodo.get(k) || 0) + (Number(p.monto) || 0));
    if (p.tipo === "sueldo") reciboEmitido.add(k);
  }

  // Orden cronológico viejo→nuevo para el FIFO.
  const orden = [...devengados].sort((a, b) => a.año - b.año || a.mes - b.mes);

  let restante = bolsa;
  let aplicadoTotal = 0;
  const subledger = orden.map(({ año, mes, devengadoMotor }) => {
    const k = `${año}-${mes}`;
    const tieneRecibo = reciboEmitido.has(k);
    // Mes cerrado → anclar al recibo registrado (Σ líquido del período). Mes abierto → motor.
    // Excepción (allowlist P3.17): con `neteoMotorDirecto` el ancla se apaga y el devengado es el
    // del motor aun en meses cerrados, para que un pago parcial no esconda el residual real.
    const devengado = tieneRecibo && !neteoMotorDirecto ? liquidoDelPeriodo.get(k) || 0 : Number(devengadoMotor) || 0;
    const aplicado = Math.min(restante, devengado);
    const saldo = devengado - aplicado;
    restante -= aplicado;
    aplicadoTotal += aplicado;
    return { año, mes, devengado, aplicado, saldo, tieneRecibo, emitir: saldo > tolerancia };
  });

  return { subledger, bolsa, aplicadoTotal, prepago: restante, tolerancia };
};
